import { getCookie } from './api';
import { getPublicEnvValue } from './env';

export type PaymentApiErrorResponse = {
  code?: string;
  message?: string;
  orgMessage?: string;
  statusCode?: number;
};

type PaymentRequestInit = Omit<RequestInit, 'credentials'> & {
  operatorId?: number | string;
  skipUnauthorizedBootstrap?: boolean;
};

type PaymentOperatorSessionResponse = {
  authenticated?: boolean;
  checkbusUid?: number | string;
  operatorId?: number | string;
};

function isPaymentOperatorSessionResponse(
  response: PaymentApiErrorResponse | PaymentOperatorSessionResponse,
): response is PaymentOperatorSessionResponse {
  return 'authenticated' in response;
}

const PAYMENT_API_PREFIX = '/v1';
const PAYMENT_OPERATOR_BOOTSTRAP_PATH = '/operator/auth/bootstrap';
const PAYMENT_OPERATOR_SESSION_PATH = '/operator/auth/session';

let paymentOperatorSessionReady = false;
let paymentOperatorSessionReadyOperatorId: number | null = null;
let paymentOperatorSessionReadyToken = '';
let ensurePaymentOperatorSessionPromise: Promise<boolean> | null = null;
let ensurePaymentOperatorSessionPromiseKey = '';

export function getPaymentApiBaseUrl(): string {
  const baseUrl = getPublicEnvValue('VITE_PAYMENT_API_URL');

  return String(baseUrl).replace(/\/+$/, '');
}

export function buildPaymentApiUrl(uri: string): string {
  const baseUrl = getPaymentApiBaseUrl();
  const normalizedUri = uri.startsWith('/') ? uri : `/${uri}`;
  const apiPrefix = baseUrl.endsWith(PAYMENT_API_PREFIX) ? '' : PAYMENT_API_PREFIX;

  return `${baseUrl}${apiPrefix}${normalizedUri}`;
}

function createPaymentHeaders(headers?: HeadersInit, hasJsonBody = false): Headers {
  const mergedHeaders = new Headers(headers);

  if (!mergedHeaders.has('Accept')) {
    mergedHeaders.set('Accept', 'application/json');
  }

  if (hasJsonBody && !mergedHeaders.has('Content-Type')) {
    mergedHeaders.set('Content-Type', 'application/json');
  }

  return mergedHeaders;
}

function normalizeOperatorId(operatorId?: number | string | null): number | null {
  const normalizedOperatorId = Number(operatorId);

  return Number.isFinite(normalizedOperatorId) && normalizedOperatorId > 0 ? normalizedOperatorId : null;
}

function getEnsurePaymentOperatorSessionKey(accessToken: string, operatorId: number | null): string {
  return `${accessToken}:${operatorId ?? ''}`;
}

function paymentSessionMatchesOperator(
  response: PaymentOperatorSessionResponse,
  expectedOperatorId: number | null,
): boolean {
  if (expectedOperatorId === null) {
    return true;
  }

  return (
    normalizeOperatorId(response.operatorId) === expectedOperatorId ||
    normalizeOperatorId(response.checkbusUid) === expectedOperatorId
  );
}

function isSuccessfulPaymentResponse(response: Response): boolean {
  return typeof response.ok === 'boolean' ? response.ok : response.status >= 200 && response.status < 300;
}

function isPaymentJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function readPaymentJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

function readPaymentErrorString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function createPaymentApiErrorResponse(response: Response, body: unknown): PaymentApiErrorResponse {
  const bodyObject = isPaymentJsonObject(body) ? body : {};
  const statusCode = response.status;
  const code =
    readPaymentErrorString(bodyObject.code) ||
    readPaymentErrorString(bodyObject.errorCode) ||
    readPaymentErrorString(bodyObject.error) ||
    `PAYMENT_API_HTTP_${statusCode}`;
  const message =
    readPaymentErrorString(bodyObject.message) ||
    readPaymentErrorString(bodyObject.orgMessage) ||
    response.statusText ||
    'Payment API request failed';
  const orgMessage = readPaymentErrorString(bodyObject.orgMessage);

  return {
    code,
    message,
    ...(orgMessage ? { orgMessage } : {}),
    statusCode,
  };
}

async function bootstrapPaymentOperatorSession(expectedOperatorId: number | null = null): Promise<boolean> {
  const accessToken = getCookie('a-token');

  if (!accessToken) {
    return false;
  }

  const response = await requestPaymentApi<PaymentOperatorSessionResponse>(PAYMENT_OPERATOR_BOOTSTRAP_PATH, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    method: 'POST',
    skipUnauthorizedBootstrap: true,
  });

  return response?.authenticated === true && paymentSessionMatchesOperator(response, expectedOperatorId);
}

async function requestPaymentApi<TResponse = unknown>(uri: string, opt?: PaymentRequestInit): Promise<TResponse> {
  const { headers: optHeaders, operatorId, skipUnauthorizedBootstrap, ...restOpt } = opt ?? {};
  const expectedOperatorId = normalizeOperatorId(operatorId);
  const hasJsonBody = restOpt.body !== undefined && restOpt.body !== null;
  const response = await fetch(buildPaymentApiUrl(uri), {
    ...restOpt,
    credentials: 'include',
    headers: createPaymentHeaders(optHeaders, hasJsonBody),
  });

  if (response.status === 401 && !skipUnauthorizedBootstrap && uri !== PAYMENT_OPERATOR_BOOTSTRAP_PATH) {
    paymentOperatorSessionReady = false;
    paymentOperatorSessionReadyOperatorId = null;
    paymentOperatorSessionReadyToken = '';

    const bootstrapped = await bootstrapPaymentOperatorSession(expectedOperatorId);

    if (bootstrapped) {
      return requestPaymentApi(uri, {
        ...opt,
        skipUnauthorizedBootstrap: true,
      });
    }
  }

  const responseBody = await readPaymentJson(response);

  if (!isSuccessfulPaymentResponse(response)) {
    return createPaymentApiErrorResponse(response, responseBody) as TResponse;
  }

  return responseBody as TResponse;
}

async function checkPaymentOperatorSession(expectedOperatorId: number | null): Promise<boolean> {
  const response = await requestPaymentApi<PaymentOperatorSessionResponse | PaymentApiErrorResponse>(
    PAYMENT_OPERATOR_SESSION_PATH,
    {
      method: 'GET',
      skipUnauthorizedBootstrap: true,
    },
  );

  if (
    isPaymentOperatorSessionResponse(response) &&
    response.authenticated === true &&
    paymentSessionMatchesOperator(response, expectedOperatorId)
  ) {
    return true;
  }

  return bootstrapPaymentOperatorSession(expectedOperatorId);
}

export async function ensurePaymentOperatorSession(operatorId?: number | string): Promise<boolean> {
  const accessToken = getCookie('a-token') || '';
  const expectedOperatorId = normalizeOperatorId(operatorId);
  const promiseKey = getEnsurePaymentOperatorSessionKey(accessToken, expectedOperatorId);

  if (!accessToken) {
    paymentOperatorSessionReady = false;
    paymentOperatorSessionReadyOperatorId = null;
    paymentOperatorSessionReadyToken = '';
    ensurePaymentOperatorSessionPromise = null;
    ensurePaymentOperatorSessionPromiseKey = '';
    return false;
  }

  if (
    paymentOperatorSessionReady &&
    paymentOperatorSessionReadyToken === accessToken &&
    (expectedOperatorId === null || paymentOperatorSessionReadyOperatorId === expectedOperatorId)
  ) {
    return true;
  }

  if (ensurePaymentOperatorSessionPromise && ensurePaymentOperatorSessionPromiseKey === promiseKey) {
    return ensurePaymentOperatorSessionPromise;
  }

  paymentOperatorSessionReady = false;
  paymentOperatorSessionReadyOperatorId = null;
  paymentOperatorSessionReadyToken = '';
  ensurePaymentOperatorSessionPromiseKey = promiseKey;
  ensurePaymentOperatorSessionPromise = checkPaymentOperatorSession(expectedOperatorId)
    .then(authenticated => {
      paymentOperatorSessionReady = authenticated;
      paymentOperatorSessionReadyToken = authenticated ? accessToken : '';
      paymentOperatorSessionReadyOperatorId = authenticated ? expectedOperatorId : null;

      return authenticated;
    })
    .finally(() => {
      ensurePaymentOperatorSessionPromise = null;
      ensurePaymentOperatorSessionPromiseKey = '';
    });

  return ensurePaymentOperatorSessionPromise;
}

export async function fetchPaymentGetApi<TResponse = unknown>(
  uri: string,
  opt?: PaymentRequestInit,
): Promise<TResponse> {
  return requestPaymentApi<TResponse>(uri, {
    ...opt,
    method: 'GET',
  });
}

export async function fetchPaymentPostApi<TResponse = unknown>(
  uri: string,
  args?: object,
  opt?: PaymentRequestInit,
): Promise<TResponse> {
  return requestPaymentApi<TResponse>(uri, {
    ...opt,
    body: args === undefined ? undefined : JSON.stringify(args),
    method: 'POST',
  });
}

export async function fetchPaymentPatchApi<TResponse = unknown>(
  uri: string,
  args?: object,
  opt?: PaymentRequestInit,
): Promise<TResponse> {
  return requestPaymentApi<TResponse>(uri, {
    ...opt,
    body: args === undefined ? undefined : JSON.stringify(args),
    method: 'PATCH',
  });
}
